import { useQueryClient } from '@tanstack/react-query'
import { Button, Col, Flex, Form, Modal, Row } from 'antd'
import { ModalProps } from 'antd/lib'
import { useEffect } from 'react'

import {
  useInvestigationsServiceCreateOrUpdateSupplyTemplateEndpoint,
  useInvestigationsServiceGetSupplyTemplateListEndpoint,
  useInvestigationsServiceGetSupplyTemplateListEndpointKey
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { SaveConfirm } from 'src/components'
import { INVESTIGATION_MEDICINE_FORM } from 'src/features/persons/utils/constants'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { InvestigationConsumablesList } from './InvestigationConsumablesList'

type Props = {
  id?: string
  testType?: string
  onCancel: () => void
} & ModalProps

export const InvestigationTemplateModal = ({
  id,
  testType,
  onCancel,
  ...props
}: Props) => {
  const { t } = useLocales()
  const queryClient = useQueryClient()

  const [form] = Form.useForm()

  const medicines = Form.useWatch('investigationMedicine', form)

  const { data, isLoading } =
    useInvestigationsServiceGetSupplyTemplateListEndpoint(
      {
        typeId: String(id)
      },
      [id],
      { enabled: !!id }
    )

  const { mutate, isPending } =
    useInvestigationsServiceCreateOrUpdateSupplyTemplateEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useInvestigationsServiceGetSupplyTemplateListEndpointKey]
        })
        onCancel()
      }
    })

  const onSave = async () => {
    const values = await form.validateFields()

    const payload = {
      supplies: values.investigationMedicine.map(
        (item: {
          dispensedAmount: number
          productFormId: string
          id: string
        }) => ({
          quantity: item.dispensedAmount,
          unitId: item.productFormId,
          productId: item.id
        })
      )
    }

    mutate({ testTypeId: String(id), requestBody: payload })
  }

  useEffect(() => {
    if (data)
      form.setFieldsValue({
        investigationMedicine: data.supplies?.map((supply) => ({
          name: supply.internationalName?.name,
          id: supply.product?.id,
          productFormName: supply.unit?.name,
          productFormId: supply.unit?.id,
          dispensedAmount: supply.quantity
        }))
      })
  }, [data])

  return (
    <Modal
      open
      centered
      loading={isLoading}
      width={700}
      maskClosable={false}
      footer={null}
      title={t('common.edit')}
      onCancel={onCancel}
      {...props}
    >
      <Form layout="vertical" requiredMark={false} form={form}>
        <Row gutter={[24, 24]}>
          <Col span={12}>
            <Form.Item name="testTypeId" label={t('tests_results.type')}>
              {testType}
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name={INVESTIGATION_MEDICINE_FORM}>
              <Form.List name={INVESTIGATION_MEDICINE_FORM}>
                {(fields, { remove }) => (
                  <InvestigationConsumablesList
                    fields={fields}
                    remove={remove}
                    form={form}
                  />
                )}
              </Form.List>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Flex justify="center" gap={8}>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>

              <SaveConfirm
                title={`${t('common.save')}?`}
                onConfirm={onSave}
                okButtonProps={{ loading: isPending }}
              >
                <Button type="primary" disabled={!medicines?.length}>
                  {t('common.save')}
                </Button>
              </SaveConfirm>
            </Flex>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
